import { Confirm } from "/WebInterface/new-ui/modules/alert.js";
import { growl } from "/WebInterface/new-ui/modules/toast.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import downloadingQueue from "/WebInterface/new-ui/modules/advanced-download/downloading-queue.js";
import { hideDownloadModal } from "/WebInterface/new-ui/modules/advanced-download/advanced-download.js";
import store from "/WebInterface/new-ui/store/index.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { extensions } from "/WebInterface/new-ui/assets/js/constants.js";
import { ajax } from "/WebInterface/new-ui/assets/js/utils/ajax.js";

const DISK_ERROR_NAME_MATCHERS = [
  "quotaexceedederror",
  "nserror_dom_quota_reached",
  "nomodificationallowederror",
  "notallowederror",
  "nserror",
];

const DISK_ERROR_MESSAGE_MATCHERS = [
  "no space",
  "disk full",
  "quota",
  "storage",
  "insufficient",
  "filesystem",
  "file system",
];

const isDiskFullError = (error) => {
  if (!error) return false;
  const name = (error.name || "").toLowerCase();
  const message = (error.message || "").toLowerCase();

  const nameMatch = DISK_ERROR_NAME_MATCHERS.some((matcher) =>
    name.includes(matcher)
  );

  const messageMatch = DISK_ERROR_MESSAGE_MATCHERS.some((matcher) =>
    message.includes(matcher)
  );

  return nameMatch || messageMatch;
};

const buildDiskWriteErrorMessage = (error) => {
  if (isDiskFullError(error)) {
    return t("failedStorageQuotaExceeded");
  }
  if (error?.message) {
    return error.message;
  }
  return t("failedWritingDownloadToDisk");
};

const handleWritableStreamError = async (parent, error) => {
  if (!parent || typeof parent.handleDiskWriteError !== "function") {
    return false;
  }
  const diskFull = isDiskFullError(error);
  const safe = {
    typeofError: typeof error,
    name: error?.name,
    message: error?.message,
    causeName: error?.cause?.name,
    causeMessage: error?.cause?.message,
  };
  C.error("Download: handleWritableStreamError debug", {
    diskFull,
    safe,
  });
  const message = buildDiskWriteErrorMessage(error);
  try {
    await parent.handleDiskWriteError(message, error, {
      diskFull,
    });
    return true;
  } catch (err) {
    C.error(`Download: Unable to handle disk write error: ${err?.message}`);
  }
  return false;
};

// Cancel downloading file
export const cancelDownload = async ({
  streamingFile,
  writableStream,
  beforeCancelingFile = () => {},
  afterCancelingFile = () => {},
}) => {
  const file = streamingFile;
  const { id: fileId } = file;

  beforeCancelingFile();

  store.dispatch("updateFileStatusDownload", {
    fileId,
    updates: {
      status: "canceled",
      downloadingError: "canceled",
    },
  });

  try {
    await writableStream.abort();
    await writableStream.close();
    try {
      await file.fileHandle.remove();
    } catch (ex) {}
  } catch (error) {
    C.log(error);
  }

  afterCancelingFile();
};

// Writes chunks in the disk using the file handle (FileSystemAPI :- FileHandle)
export const writeDownloadedChunksToDisk = async (parent) => {
  if (parent.fileClosed) {
    C.warn("Download: File is closed, continuing writing chunks to disk");
  }
  C.store("Download: write chunks to disk called");
  const this_parent = parent;

  const { size: fileSize, id: fileId } = this_parent.streamingFile;
  const writableStream = this_parent.writableStream;

  // Writes file in the disk while chain of the chunks break
  while (true) {
    const index = this_parent.chunkWrittenToTheDisk;
    const chunk = this_parent.chunksRemainsToWrite[index];

    if (!chunk) {
      C.store(
        `Download: expected index not available in chunksRemainsToWrite so breaking the loop ${index}`
      );
      const downloadDone =
        Object.keys(this_parent.chunksBeingWritten).filter(
          (key) =>
            !Object.prototype.hasOwnProperty.call(
              this_parent.chunksBeingWritten,
              key
            )
        ).length === 0 &&
        Object.keys(this_parent.chunksRemainsToWrite).filter(
          (key) =>
            !Object.prototype.hasOwnProperty.call(
              this_parent.chunksRemainsToWrite,
              key
            )
        ).length === 0;
      // If file completed to write close it
      if (this_parent.fileDownloaded && downloadDone) {
        C.warn(
          `Download: Interesting case when EOF received but index mismatches, Nothing left to write, nothing being written. Last received: ${this_parent.lastReceivedChunkIndex}, Last written: ${this_parent.lastWrittenChunkIndex}`
        );

        if (!this_parent.closeFileInProgress) {
          this_parent.closeFileDownloading();
          C.warn(`Download: File download done, breaking the loop`);
        }
      }
      break;
    } else {
      const { data, size } = chunk;
      try {
        this_parent.lastWrittenChunkIndex = index;
        C.log(`Download: Writing chunk ${index}`);
        this_parent.chunkWrittenToTheDisk++;
        this_parent.chunksBeingWritten[index] = index;
        await writableStream.write(data);
        this_parent.chunksWritten[index] = index;
        delete this_parent.chunksBeingWritten[index];
        C.log(`Download: Wrote chunk ${index}`);
        this_parent.lastIndexOfChunkWrittenToDisk = index;
        this_parent.bytesWritten += size;
        this_parent.lastDiskWriteTime = Date.now();
        this_parent.lastDiskWriteBytesWritten = this_parent.bytesWritten;

        C.log(
          `Download: Written ${
            this_parent.bytesWritten
          }/${fileSize} (${getPercentage(fileSize, this_parent.bytesWritten)}%)`
        );

        if (!this_parent.fileClosed && !this_parent.fileDownloaded) {
          store.dispatch("updateFileStatusDownloadDisk", {
            fileId,
            bytesWritten: this_parent.bytesWritten,
            fileSize,
          });
        }

        delete this_parent.chunksRemainsToWrite[index];

        const downloadDone =
          Object.keys(this_parent.chunksBeingWritten).filter(
            (key) =>
              !Object.prototype.hasOwnProperty.call(
                this_parent.chunksBeingWritten,
                key
              )
          ).length === 0 &&
          Object.keys(this_parent.chunksRemainsToWrite).filter(
            (key) =>
              !Object.prototype.hasOwnProperty.call(
                this_parent.chunksRemainsToWrite,
                key
              )
          ).length === 0;
        this_parent.checkIfFileDownloaded();
        // If file completed to write close it
        if (this_parent.fileDownloaded) {
          if (downloadDone) {
            if (!this_parent.closeFileInProgress) {
              this_parent.closeFileDownloading();
              C.warn(`Download: File download done, breaking the loop`);
            }
            break;
          } else {
            C.warn(
              `Download: File downloaded, but chunks are still remaining to write, continuing the loop`
            );
          }
        }
        // C.log(
        //   `Download: Chunk written to disk ${index}, next index is ${this_parent.chunkWrittenToTheDisk}`
        // );
      } catch (error) {
        C.error("Download: Error in write chunk to disk: " + error);
        C.error("Download: write chunk error details", {
          name: error?.name,
          message: error?.message,
          causeName: error?.cause?.name,
          causeMessage: error?.cause?.message,
        });
        const handled = await handleWritableStreamError(this_parent, error);
        if (handled) {
          C.log("Download: Writable stream error handled");
          return;
        }
        C.error("Download: Writable stream error not handled");
        break;
      }
    }
  }

  let pendingChunks = 0;
  let sumOfSize = 0;
  let pendingChunkNo = [];
  const bytesToMegabytes = (bytes) => {
    return bytes / (1024 * 1024);
  };
  const beingWritten = [];
  for (const key in this_parent.chunksBeingWritten) {
    if (this_parent.chunksBeingWritten.hasOwnProperty(key)) {
      beingWritten.push(key);
    }
  }
  for (const key in this_parent.chunksRemainsToWrite) {
    if (this_parent.chunksRemainsToWrite.hasOwnProperty(key)) {
      if (beingWritten.indexOf(key) < 0) {
        pendingChunks++;
        pendingChunkNo.push(key);
        sumOfSize += this_parent.chunksRemainsToWrite[key].size;
      }
    }
  }

  if (beingWritten.length > 0) {
    C.log(
      `Download: Total ${
        beingWritten.length
      } chunks are being written, no: ${beingWritten.sort().join(",")}`
    );
  }

  if (pendingChunks) {
    C.log(
      `Download: Total ${pendingChunks} not written chunks, size: ${bytesToMegabytes(
        sumOfSize
      )} MB, Last Written Index ${
        this_parent.lastIndexOfChunkWrittenToDisk
      }, no: ${pendingChunkNo.sort().join(",")}`
    );
  }
};

// Calculate speed and time remaining to download the file
export const updateTimeStatusOfDownloadingFileCommon = (parent) => {
  C.store("Download: update time status of downloading file called");
  const this_parent = parent;
  const { id: fileId, size } = this_parent.streamingFile;

  clearInterval(this_parent.elapsedIntervalId);

  this_parent.elapsedIntervalId = setInterval(() => {
    if (this_parent.fileClosed) {
      clearInterval(this_parent.elapsedIntervalId);
      return;
    }
    const currentSpeedSource =
      typeof this_parent.bytesReceivedForSpeed === "number"
        ? this_parent.bytesReceivedForSpeed
        : this_parent.bytesDownloaded;
    const lastSpeedSource =
      typeof this_parent.lastBytesReceivedForSpeed === "number"
        ? this_parent.lastBytesReceivedForSpeed
        : this_parent.lastBytesDownloaded;
    // Download speed calc - bytes per second (use received bytes to avoid ACK batching spikes)
    const speed = (this_parent.speed = currentSpeedSource - lastSpeedSource);

    // Overall speed
    const overAllSpeed = this_parent.bytesDownloaded / this_parent.elapsedTime;

    const speedToCheckTimeRemaining = speed || overAllSpeed;

    // time remaining calc - remaining size to download divides by current download speed
    const timeRemaining = parseInt(
      (parseInt(size) - this_parent.bytesDownloaded) / speedToCheckTimeRemaining
    );

    // elapsed time calc - adding second on every interval
    const elapsedTime = (this_parent.elapsedTime += 1);

    this_parent.lastBytesDownloaded = this_parent.bytesDownloaded;
    if (typeof this_parent.bytesReceivedForSpeed === "number") {
      this_parent.lastBytesReceivedForSpeed = currentSpeedSource;
    }

    // C.log(
    //   "updateTimeStatusOfDownloadingFileCommon",
    //   this_parent.streamingFile
    // );

    C.store(
      `Download: Update file information on UI ID: ${fileId} Speed: ${speed} ETime: ${elapsedTime} TRemain: ${timeRemaining} BytesWritten: ${this_parent.bytesWritten}`
    );
    store.dispatch("updateTimeStatusDownload", {
      fileId,
      bytesWritten: this_parent.bytesWritten,
      speed,
      elapsedTime,
      timeRemaining,
    });
  }, 1000);

  clearInterval(this_parent.intervalIdAverageTenSecondSpeed);

  this_parent.intervalIdAverageTenSecondSpeed = setInterval(() => {
    if (this_parent.fileClosed) {
      clearInterval(this_parent.intervalIdAverageTenSecondSpeed);
      return;
    }
    // Download speed calc - bytes per 10 seconds
    const speed =
      (this_parent.average10SecSpeed =
        this_parent.bytesDownloaded - this_parent.lastBytesDownloadedInTenSec) /
      10;

    this_parent.lastBytesDownloadedInTenSec = this_parent.bytesDownloaded;

    C.store(
      `Download: Update avg speed info to UI ID: ${fileId} Speed: ${speed}`
    );

    store.dispatch("updateTenSecondAverageDownload", {
      fileId,
      speed,
    });
  }, 10000);
};

const isFallbackCustomizationEnabled = (key) => {
  const customizations = store.userInfo?.userCustomizations;
  if (!customizations) return false;
  const camelKey =
    key === "fallbacktoZipDownload"
      ? "fallbackToZipDownload"
      : key === "fallbacktoNormalDownload"
        ? "fallbackToNormalDownload"
        : key;
  const raw = customizations[key]?.value ?? customizations[camelKey]?.value;
  if (raw === undefined || raw === null) return false;
  const v = String(raw).toLowerCase();
  return v === "true" || v === "1" || v === "yes" || raw === true;
};

/**
 * Attempts fallback (zip or normal download) when customization is enabled.
 * Logs originalError to console when fallback runs; returns message for UI so caller can show friendly text.
 * @param {object} file - Current file (id, href_path, name)
 * @param {string} [originalError] - Original error message (logged to console when fallback runs)
 * @returns {{ triggered: boolean, message?: string }} - triggered true and message when fallback ran, else { triggered: false }
 */
export const fallbackToZipDownload = (file, originalError) => {
  const fileId = file?.id;
  const pathOrName = file?.href_path || file?.name || "unknown";

  C.log(
    `Download: Fallback attempted (fileId: ${fileId || "n/a"}, path: ${pathOrName})`
  );

  if (!file || !file.href_path) {
    C.log("Download: Fallback skipped (no file or href_path)");
    return { triggered: false };
  }

  const useZip = isFallbackCustomizationEnabled("fallbacktoZipDownload");
  const useNormal = isFallbackCustomizationEnabled("fallbacktoNormalDownload");

  if (!useZip && !useNormal) {
    C.log(
      `Download: Fallback skipped (fallbacktoZipDownload=${useZip}, fallbacktoNormalDownload=${useNormal})`
    );
    return { triggered: false };
  }

  const path = utils.encodeURILocal(file.href_path);

  try {
    if (originalError) {
      C.warn(`Download: Original error (fallback enabled): ${originalError}`);
    }
    if (useZip) {
      const zipName =
        file.name && !file.name.endsWith(".zip")
          ? file.name + ".zip"
          : file.name || "archive.zip";
      C.log(
        `Download: Fallback using zip (zipName: ${zipName}, path: ${file.href_path})`
      );
      ajax.iframeSubmit({
        "#command": "downloadAsZip",
        "#zipName": zipName,
        "#paths": path,
      });
      C.log(
        `Download: Advanced download failed; fallback to zip download triggered for: ${file.name || file.href_path}`
      );
      hideDownloadModal();
      return { triggered: true, message: "Switching to zip download" };
    }
    if (useNormal) {
      C.log(
        `Download: Fallback using normal download (path: ${file.href_path})`
      );
      ajax.iframeSubmit({
        "#command": "download",
        "#path": path,
      });
      C.log(
        `Download: Advanced download failed; fallback to normal download triggered for: ${file.name || file.href_path}`
      );
      hideDownloadModal();
      return { triggered: true, message: "Switching to normal download" };
    }
  } catch (err) {
    C.warn(`Download: Fallback download failed: ${err?.message || err}`);
  }
  return { triggered: false };
};

// Handle retry of the failed chunks
export const handleDownloadRetry = async (
  parent,
  retries,
  cb = () => {},
  onRetryOver = () => {}
) => {
  const this_parent = parent;
  const file = this_parent.streamingFile;
  const { id: fileId, name: fileName } = file;

  // Return if already retrying chunks
  if (this_parent.retrying) return;
  this_parent.retrying = true;

  clearInterval(this_parent.elapsedIntervalId);
  clearInterval(this_parent.intervalIdAverageTenSecondSpeed);

  let retrySeconds = (retries + 1) * 10;
  let retryIntervalId;

  const setRetryTimer = (close) => {
    const intervalId = setInterval(() => {
      retrySeconds--;
      _$("#retrySeconds").text(retrySeconds);

      if (retrySeconds === 0) {
        this_parent.retrying = false;
        clearInterval(retryIntervalId);
        cb();
        close();
      }
    }, 1000);

    return intervalId;
  };

  const onBeforeShow = (root, close) => {
    retryIntervalId = setRetryTimer(close);
  };

  const onAfterShow = (root, close) => {
    _$("#skipFile").on("click", () => {
      clearInterval(retryIntervalId);

      const skipFile = window.confirm(
        `Are you sure you want to cancel (${fileName}) file from downloading?`
      );

      if (skipFile) {
        close();
        this_parent.cancel();
        return;
      }

      retryIntervalId = setRetryTimer(close);
    });
  };

  const prepend = `<p class="text-primaryMain">Retrying downloading file <b>(${utils.textEncode(
    fileName
  )})</b> In <span id="retrySeconds">${retrySeconds}</span> second(s)</p>`;

  try {
    const resp = await Confirm({
      title: "Retry",
      prepend,
      cancel: null,
      confirm: "Retry Now",
      closable: false,
      prependFooter: utils.generateHTML([
        {
          type: "button#skipFile.primary-button.is-bordered.mr-3",
          text: "Skip File",
        },
      ]),
      onBeforeShow,
      onAfterShow,
    });

    clearInterval(retryIntervalId);
    this_parent.retrying = false;

    if (resp) {
      if (retries < this_parent.maxRetries) {
        cb();
        return;
      }

      onRetryOver();

      const retryErrorMsg = "Failed while retrying chunk download";
      const fallbackResult = fallbackToZipDownload(
        this_parent.streamingFile,
        retryErrorMsg
      );
      store.dispatch("updateFileStatusDownload", {
        fileId,
        updates: {
          status: "failed",
          downloadingError: fallbackResult.triggered
            ? fallbackResult.message
            : "Downloading failed : " + retryErrorMsg,
        },
      });

      this_parent.nextFileInQueue();
      return;
    }
  } catch (error) {}
};

// Resume file downloading after pause
export const resumeDownload = (parent, onResume = () => {}) => {
  const this_parent = parent;
  const { isUploading } = store.chunkProcessingStatus;

  if (isUploading) {
    growl(
      t("cantResumeDownloadingTillUploadingFinished"),
      t("uploadingInProgress"),
      5000,
      true
    );
    return;
  }

  const file = this_parent.streamingFile;
  const { id: fileId } = file;

  this_parent.isPause = false;

  store.dispatch("updateFileStatusDownload", {
    fileId,
    updates: {
      status: "downloading",
    },
  });

  onResume();
};

// Triggers after successful pause of the downloading file
export const pausedDownload = (parent) => {
  const this_parent = parent;
  const file = this_parent.streamingFile;
  const { id: fileId } = file;

  clearInterval(this_parent.elapsedIntervalId);
  clearInterval(this_parent.intervalIdAverageTenSecondSpeed);

  store.dispatch("updateFileStatusDownload", {
    fileId,
    updates: {
      status: "paused",
      timeStatus: {
        elapsedTime: this_parent.elapsedTime,
        speed: 0,
        timeRemaining: null,
        avg10SecSpeed: 0,
      },
    },
  });

  downloadingQueue.handleUpdateDownloadingStatus(false);
};

// pauses downloading file
export const pauseDownload = (parent, onPause = () => {}) => {
  const this_parent = parent;

  const file = this_parent.streamingFile;
  const { id: fileId } = file;

  onPause();

  this_parent.isPause = true;

  store.dispatch("updateFileStatusDownload", {
    fileId,
    updates: {
      status: "pausing",
    },
  });
};

export const getFileMime = (fileName) => {
  // Extract the file extension
  const extension = fileName.split(".").pop().toLowerCase();

  // Define the accept object
  const accept = {};

  // Check if the file extension is supported
  if (extensions.hasOwnProperty(extension)) {
    accept[extensions[extension]] = ["." + extension];
  } else {
    // If the extension is not supported, default to 'application/octet-stream'
    accept["*/*"] = [];
  }
  console.log(accept);
  // Return the object
  return { accept };
};

export const chunkNosFromList = (list) => {
  const _list = [];
  for (const key in list) {
    if (list.hasOwnProperty(key)) {
      _list.push(parseInt(key));
    }
  }
  return _list.sort((a, b) => a - b);
};

export const findRemainingChunks = (numbers) => {
  const missingNumbers = [];
  // Iterate through the array starting from the second element
  for (let i = 1; i < numbers.length; i++) {
    // Calculate the difference between consecutive numbers
    const diff = numbers[i] - numbers[i - 1];
    // If the difference is greater than 1, there is a gap
    if (diff > 1) {
      // Add the missing numbers to the missingNumbers array
      for (let j = numbers[i - 1] + 1; j < numbers[i]; j++) {
        missingNumbers.push(j);
      }
    }
  }
  return missingNumbers;
};

export const getPercentage = (fileSize, bytesReceived) => {
  if (fileSize === 0) {
    return 0;
  }
  const progress = Math.floor((bytesReceived / fileSize) * 100);
  // Cap at 99% to ensure 100% only shows when truly complete
  return Math.min(progress, 99);
};
